import Logger from '@utils/log'
import LabelStudio from '@parse-ai/label-studio'
import { createLabelStudioConfig } from './label-studio-config'
import * as htmlEntities from 'html-entities'
import { loadImageWithAuthorization } from '@utils'
import Tesseract from 'tesseract.js'

const log = new Logger('utils/label-studio')
const img = new Image()

const toggleButton = disabled => {
    // eslint-disable-next-line no-restricted-globals
    const btn = document.querySelector('.lsf-editor .lsf-button_look_primary')
    
    if (disabled) {
        btn.disabled = true
        btn.dataset.processCount = parseInt(btn.dataset.processCount || 0, 10) + 1
        btn.style.opacity = 0.7
        btn.innerText = 'Loading OCR...'
    } else {
        btn.dataset.processCount = parseInt(btn.dataset.processCount || 1, 10) - 1
        
        if (parseInt(btn.dataset.processCount, 10) === 0) {
            btn.disabled = false
            btn.style.opacity = ''
            btn.innerText = 'Update'
        }
    }
}

export const createTask = initialTask => {
    
    return {
        ...initialTask,
        annotations: initialTask.predictions,
        predictions: [],
    }
    
}

export const formatValidXmlAttr = value => htmlEntities.encode(value, {
    level: 'xml',
    mode: 'nonAsciiPrintable',
})

export const createLabels = (task, initialLabels = []) => {
    
    /* const labels = task.annotations.reduce((acc, annotation) => {
        
        annotation.result.forEach(result => {
            result.value?.labels?.forEach(label => acc.add(label))
        })
        
        return acc
        
    }, new Set(initialLabels)) */
    
    const labels = initialLabels.reduce((acc, it) => ({
        ...acc,
        [it.label]: {
            ...it,
            label: formatValidXmlAttr(it.label),
            value: formatValidXmlAttr(it.value),
        },
    }), {})
    
    // left it commented because who knows this needs to exist again
    // task.annotations.forEach(annotation => {
    //     annotation.result.forEach(result => {
    //         result.value?.labels?.forEach(label => {
    
    //             // Labels derived from existing annotations (i.e. not manually created in our database)
    //             // don't have an ID or object structure. Since we don't actually need the IDs here, we can fake it
    //             if (!Object.hasOwn(labels, label)) {
    //                 labels[label] = {
    //                     id: null,
    //                     label: formatValidXmlAttr(label),
    //                 }
    //             }
    //         })
    //     })
    // })
    
    // console.log('createLabels', { initialLabels, labels })
    
    return Object.values(labels).map(it => `
        <Label
            value="${it.label}"
            background="${it.color ?? 'blue'}" />
    `)
    
}

const extrapolateValues = (scaledValues, zoom) => {
    
    const scaleWidth = img.width / scaledValues.elWidth
    const scaleHeight = img.height / scaledValues.elHeight
    
    return {
        x: scaledValues.x * scaleWidth * zoom,
        y: scaledValues.y * scaleHeight * zoom,
        width: scaledValues.width * scaleWidth * zoom,
        height: scaledValues.height * scaleHeight * zoom,
    }
}

const drawLabelImage = region => {
    
    const { x, y, width, height } = region
    const { width: elWidth, height: elHeight } = region.object.imageRef
    const zoom = region.object.currentZoom
    const values = extrapolateValues({ x, y, width, height, elWidth, elHeight }, zoom)
    
    // eslint-disable-next-line no-restricted-globals
    const canvas = document.createElement('canvas')
    
    canvas.width = values.width
    canvas.height = values.height
    canvas.getContext('2d').drawImage(img, values.x, values.y, values.width, values.height,
        0, 0, canvas.width, canvas.height)
    
    toggleButton(true)
    
    Tesseract.recognize(
        canvas.toDataURL(),
        'eng',
        
        // { logger: m => console.log(m) } // logs progress of recognition
    ).then(({ data: { text } }) => {
        const parsedText = text.replace(/\n/g, ' ').trim()
        
        // it worked with all those values commented; left them there for future ref, and if its needed for backend
        region.addResult({
            type: 'textarea',
            from_name: 'transcription',
            to_name: 'image',
            
            // "image_rotation": 0,
            // "original_height": img.height,
            // "original_width": img.width,
            value: {
                
                // "x": values.x / img.width * 100,
                // "y": values.y / img.height * 100,
                // "width": values.width / img.width * 100,
                // "height": values.height / img.height * 100,
                // "rotation": 0,
                text: [parsedText],
            },
        })
        
        toggleButton(false)
    }).catch(err => log.e(err))
    
}

export const initializeLabelStudio = (
    initialTask,
    initialLabels = [],
    onUpdateAnnotation,
    interfaces = null,
    onLoad = null,
) => {
    
    const task = createTask(initialTask)
    const labels = createLabels(task, initialLabels)
    const config = createLabelStudioConfig(labels.join('\n'))
    
    log.d('initializeLabelStudio', {
        initialTask,
        initialLabels,
        onUpdateAnnotation,
        interfaces,
        onLoad,
        
        task,
        labels,
        config,
    })
    
    const labelStudio = new LabelStudio('LabelStudio-editor', {
        
        config,
        
        interfaces: interfaces || [
            // 'panel',
            // 'skip',
            'update',
            // 'submit',
            'controls',
            // 'infobar',
            'topbar',
            // 'instruction',
            'side-column',
            // 'annotations:tabs',
            // 'annotations:menu',
            // 'annotations:current',
            // 'annotations:add-new',
            // 'annotations:delete',
            // 'predictions:tabs',
            // 'predictions:menu',
            // 'auto-annotation',
            // 'edit-history',
        ],
        
        
        /* userLabels: [
            { id: 1, value: 'Foo', isLabel: true, item: { id: 1, value: 'Foo', isLabel: true } },
        ], */
        
        task,
        
        // Event handlers
        // @see https://github.com/heartexlabs/label-studio/
        //      blob/6c73bcc1490fcc21e2dde6de90ceca69a258f277/docs/source/guide/frontend_reference.md
        
        // eslint-disable-next-line no-unused-vars
        onLabelStudioLoad: ls => {
            
            log.d('onLabelStudioLoad task', task)
            
            // @todo can this `ocr` be any other tag? From the current information, this is the image URL
            log.d('Loading async image for LS', task.data.ocr)
            loadImageWithAuthorization(task.data.ocr)
                .then(({ dataUrl }) => {
                    log.d('Loaded async image for LS', dataUrl)
                    img.src = dataUrl
                })
                .catch(e => log.e('Failed to load async image for LS', e))
            
            /* const debug = task.annotations.flatMap(it => (
                it.result.flatMap(x => [
                    x.value?.labels,
                    x.value?.text,
                ])
            ))
            
            log.d('onLabelStudioLoad task', JSON.stringify(debug, null, 4)) */
            
            if (onLoad) onLoad()
            
        },
        
        onSubmitAnnotation: (ls, annotation) => {
            log.d('onSubmitAnnotation', ls, annotation)
        },
        
        onUpdateAnnotation: (ls, annotation) => {
            
            const serialized = annotation.serializeAnnotation()
            
            onUpdateAnnotation(ls, annotation, serialized)
            
        },
        
        onDeleteAnnotation: (ls, annotation) => {
            log.d('onDeleteAnnotation', ls, annotation)
        },
    })
    
    // Just for now, we will disable the Label as default view
    // When we get a better solution to accomplish this needed behavior, we
    // can turn back to Label as default view
    if (localStorage.getItem('regionstore:view')) localStorage.removeItem('regionstore:view')
    // localStorage.setItem('regionstore:view', 'labels')
    labelStudio.on('entityCreate', drawLabelImage)
    labelStudio.on('entityDelete', region => log.d('onEntityDelete', region))
    
    /* labelStudio.on('deleteAnnotation', (ls, annotation) => {
        log.d('onDeleteAnnotation', ls, annotation)
    })
    labelStudio.on('entityDelete', (region) => log.d('entityDelete', region)) */
    
    return labelStudio
    
}
